/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { PrivateLayout, PV_THEME, usePvMode } from '../PrivateLayout';
import {
  Storefront,
  Wallet,
  Handshake,
  ArrowsLeftRight,
  ShieldStar,
  ArrowSquareOut,
  ArrowRight,
  Images,
  Diamond,
  Certificate,
  Check,
} from '@phosphor-icons/react';

const T = PV_THEME;

function CompanyCard({ num, name, accent, badge, summary, italic, links, outcome, users, moat }) {
  return (
    <div
      className="rounded-[6px] overflow-hidden transition-colors duration-300"
      style={{ background: T.surface, border: `1px solid ${T.border}` }}
    >
      <div className="flex items-stretch">
        <div className="w-1 shrink-0" style={{ background: accent }} />
        <div className="p-5 sm:p-6 flex-1">
          <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
            <div>
              <div className="font-mono text-[8px] tracking-[0.25em] uppercase mb-1" style={{ color: accent }}>
                {num}
              </div>
              <h3 className="font-mono uppercase" style={{ fontSize: 15, letterSpacing: '0.15em', color: T.text }}>
                {name}
              </h3>
            </div>
            <span
              className="font-mono uppercase whitespace-nowrap"
              style={{
                fontSize: 8,
                letterSpacing: '0.12em',
                color: accent,
                border: `1px solid ${accent}33`,
                background: `${accent}0d`,
                padding: '4px 8px',
              }}
            >
              {badge}
            </span>
          </div>
          <p className="leading-[1.9] mb-3" style={{ fontSize: 12, color: T.textMuted }}>
            {summary}
          </p>
          {italic && (
            <p className="font-mono italic mb-4" style={{ fontSize: 10, color: `${accent}cc` }}>
              {italic}
            </p>
          )}
          <div className="flex gap-2 mb-5 flex-wrap">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 transition-colors hover:opacity-80"
                style={{
                  fontSize: 8,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: accent,
                  border: `1px solid ${accent}4d`,
                  padding: '6px 12px',
                  fontFamily: 'monospace',
                }}
              >
                <ArrowSquareOut size={10} weight="bold" />
                {l.label}
              </a>
            ))}
          </div>
          <div
            className="grid gap-4 pt-4"
            style={{ borderTop: `1px solid ${T.border}`, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}
          >
            <Cell label="Outcome sold" value={outcome} highlight />
            <Cell label="Core users" value={users} />
            {moat && <Cell label="Moat" value={moat} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, highlight }) {
  return (
    <div>
      <div className="font-mono uppercase mb-1.5" style={{ fontSize: 8, letterSpacing: '0.2em', color: T.textFaint }}>
        {label}
      </div>
      <div
        className="font-mono"
        style={{ fontSize: 9, color: highlight ? T.text : T.textMuted, lineHeight: 1.5 }}
      >
        {value}
      </div>
    </div>
  );
}

// ── Diagram primitives (hoisted to avoid react/no-unstable-nested-components) ──
function DiagramNode({ rgb, label, sub, Icon, isLight }) {
  const fill = isLight ? `rgba(${rgb},0.14)` : `rgba(${rgb},0.1)`;
  const border = `rgb(${rgb})`;
  const glow = isLight
    ? `0 0 0 1px rgb(${rgb}), 0 8px 24px rgba(${rgb},0.25), inset 0 0 0 1px rgba(255,255,255,0.6)`
    : `0 0 30px rgba(${rgb},0.2), inset 0 1px 0 rgba(255,255,255,0.04)`;
  return (
    <div
      className="rounded-[6px] px-6 py-5 text-center transition-all duration-300"
      style={{ border: `1px solid ${border}`, background: fill, boxShadow: glow }}
    >
      <div className="font-mono uppercase mb-1 flex items-center justify-center gap-2" style={{ fontSize: 12, letterSpacing: '0.22em', color: border, fontWeight: 700 }}>
        {Icon && <Icon size={14} weight="bold" />}{label}
      </div>
      <div className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: '0.15em', color: border, opacity: isLight ? 0.78 : 0.6, fontWeight: 600 }}>
        {sub}
      </div>
    </div>
  );
}

function DiagramConnector({ color = '99,102,241', isLight }) {
  return (
    <div className="flex flex-col items-center py-2 relative" aria-hidden>
      <div
        className="w-0.5 h-10 relative overflow-hidden"
        style={{ background: `linear-gradient(to bottom, rgba(${color},0) 0%, rgb(${color}) 30%, rgb(${color}) 70%, rgba(${color},0) 100%)` }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, transparent 0%, rgba(255,255,255,${isLight ? 0.5 : 0.8}) 50%, transparent 100%)`,
            animation: 'pvFlow 2.2s linear infinite',
          }}
        />
      </div>
      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: `8px solid rgb(${color})`,
          filter: `drop-shadow(0 0 4px rgba(${color},0.6))`,
        }}
      />
    </div>
  );
}

export default function EcosystemSection() {
  const [mode] = usePvMode();
  const isLight = mode === 'light';

  return (
    <PrivateLayout eyebrowLabel="The Ecosystem" testid="pv-ecosystem">
      {/* Ambient glows */}
      <div
        className="fixed top-0 left-1/4 w-[600px] h-[400px] rounded-full pointer-events-none z-0"
        style={{ background: 'rgba(168,85,247,0.05)', filter: 'blur(120px)' }}
      />
      <div
        className="fixed bottom-1/4 right-1/3 w-[400px] h-[300px] rounded-full pointer-events-none z-0"
        style={{ background: 'rgba(77,166,255,0.05)', filter: 'blur(100px)' }}
      />

      <section className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-12">
        <div className="font-mono uppercase mb-4" style={{ fontSize: 9, letterSpacing: '0.35em', color: T.textFaint }}>
          Origin · 2022 — Present
        </div>
        <h1
          className="font-mono uppercase mb-4"
          style={{ fontSize: 'clamp(28px, 5vw, 48px)', letterSpacing: '0.12em', color: T.text, lineHeight: 1.1 }}
        >
          Secure Art Transactions<br />
          <span style={{ color: T.accent, textShadow: '0 0 12px rgba(245,166,35,0.4)' }}>Built on Trust</span>
        </h1>
        <p className="leading-[2] max-w-2xl mb-6" style={{ fontSize: 13, color: T.textMuted }}>
          TrustFrame provides authentication, compliance, escrow and provenance infrastructure for high-value art sales. Protect collectors, galleries and estates with{' '}
          <em className="not-italic" style={{ color: T.text }}>neutral trust infrastructure</em>.
        </p>
      </section>

      {/* Diagram */}
      <section className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="font-mono uppercase mb-1" style={{ fontSize: 8, letterSpacing: '0.3em', color: T.textFaint }}>
          Architecture
        </div>
        <div className="flex items-center gap-4 mb-10">
          <span className="font-mono uppercase" style={{ fontSize: 11, letterSpacing: '0.2em', color: T.purple }}>
            The TrustFrame Family of Companies
          </span>
          <div className="h-px flex-1" style={{ background: `linear-gradient(to right, ${T.border}, transparent)` }} />
        </div>

        <div className="flex flex-col items-center gap-0 mb-12 select-none">
          {/* Monyx.cafe */}
          <div className="w-full max-w-sm">
            <DiagramNode rgb="124,58,237" label="Monyx.cafe" sub="Storefront — the front door" Icon={Storefront} isLight={isLight} />
          </div>

          <DiagramConnector color="124,58,237" isLight={isLight} />

          {/* Monyx.money */}
          <div className="w-full max-w-sm">
            <DiagramNode rgb="5,150,105" label="Monyx.money" sub="Wallet, custody, future bank" Icon={Wallet} isLight={isLight} />
          </div>

          <div className="py-4 text-center">
            <span
              className="font-mono uppercase inline-block px-3 py-1.5"
              style={{
                fontSize: 9,
                letterSpacing: '0.3em',
                color: T.text,
                background: isLight ? 'rgba(0,47,167,0.06)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${T.borderFaint}`,
                fontWeight: 600,
              }}
            >
              Three managed trust services
            </span>
          </div>

          <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
            {[
              { name: 'TrustFrame', sub: 'Protected art\ntransactions', rgb: '225,29,72', Icon: ShieldStar },
              { name: 'Custos', sub: 'Wealth transfer\nfor families', rgb: '225,29,72', Icon: Handshake },
              { name: 'Custodian', sub: 'Succession\nhandover', rgb: '225,29,72', Icon: ArrowsLeftRight },
            ].map(({ name, sub, rgb, Icon }) => {
              const border = `rgb(${rgb})`;
              const fill = isLight ? `rgba(${rgb},0.10)` : `rgba(${rgb},0.08)`;
              return (
                <div
                  key={name}
                  className="rounded-[6px] px-4 py-4 text-center transition-all duration-300"
                  style={{
                    border: `1px solid ${border}`,
                    background: fill,
                    boxShadow: isLight
                      ? `0 6px 20px rgba(${rgb},0.18), inset 0 0 0 1px rgba(255,255,255,0.55)`
                      : `0 0 20px rgba(${rgb},0.15), inset 0 1px 0 rgba(255,255,255,0.03)`,
                  }}
                >
                  <div className="font-mono uppercase mb-1.5 flex items-center justify-center gap-1.5" style={{ fontSize: 11, letterSpacing: '0.18em', color: border, fontWeight: 700 }}>
                    <Icon size={12} weight="bold" />
                    {name}
                  </div>
                  <div className="font-mono uppercase whitespace-pre-line" style={{ fontSize: 8, letterSpacing: '0.1em', color: border, opacity: isLight ? 0.75 : 0.6, lineHeight: 1.6, fontWeight: 600 }}>
                    {sub}
                  </div>
                </div>
              );
            })}
          </div>

          <DiagramConnector color="225,29,72" isLight={isLight} />

          {/* Sub-tree: TrustFrame breaks into 3 products */}
          <div className="py-3 text-center">
            <span
              className="font-mono uppercase inline-block px-3 py-1.5"
              style={{
                fontSize: 9,
                letterSpacing: '0.3em',
                color: T.text,
                background: isLight ? 'rgba(0,47,167,0.06)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${T.borderFaint}`,
                fontWeight: 600,
              }}
            >
              TrustFrame breaks into three products
            </span>
          </div>

          <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
            {[
              { name: 'TrustFrame Studio', stage: 'NOW · UNREGULATED', sub: 'Enrolment engine — provenance & certification. Feeds both regulated products.', rgb: isLight ? '21,128,61' : '0,214,143', Icon: Images },
              { name: 'TrusTEE', stage: 'NEXT · PROTOTYPE READY', sub: 'Asset-backed lending against verified works. Generates recovery data.', rgb: isLight ? '196,92,35' : '245,166,35', Icon: Diamond },
              { name: 'TrustFrame Schedule', stage: 'THIRD · DEEPEST MOAT', sub: 'Cover activated from the scheduled register. Recurring premium.', rgb: isLight ? '0,47,167' : '77,166,255', Icon: Certificate },
            ].map(({ name, stage, sub, rgb, Icon }) => {
              const border = `rgb(${rgb})`;
              return (
                <div
                  key={name}
                  className="rounded-[6px] px-4 py-4 text-center relative overflow-hidden transition-all duration-300"
                  style={{
                    border: `1.5px solid ${border}`,
                    background: isLight ? '#ffffff' : `rgba(${rgb},0.08)`,
                    boxShadow: isLight
                      ? `0 8px 24px rgba(${rgb},0.22), inset 0 0 0 1px rgba(${rgb},0.08)`
                      : `0 0 24px rgba(${rgb},0.18), inset 0 1px 0 rgba(255,255,255,0.04)`,
                  }}
                >
                  <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: border, boxShadow: `0 0 8px ${border}` }} />
                  <div className="font-mono uppercase mb-2 flex items-center justify-center gap-1.5 pt-2" style={{ fontSize: 10, letterSpacing: '0.17em', color: border, fontWeight: 700 }}>
                    <Icon size={12} weight="fill" />
                    {name}
                  </div>
                  <div
                    className="font-mono uppercase inline-block mb-2 px-2 py-0.5"
                    style={{
                      fontSize: 7.5,
                      letterSpacing: '0.22em',
                      color: border,
                      background: isLight ? `rgba(${rgb},0.08)` : `rgba(${rgb},0.12)`,
                      border: `1px solid ${border}`,
                      fontWeight: 700,
                    }}
                  >
                    {stage}
                  </div>
                  <div className="font-sans" style={{ fontSize: 10, lineHeight: 1.6, color: T.text, opacity: 0.85 }}>
                    {sub}
                  </div>
                </div>
              );
            })}
          </div>

          <DiagramConnector color={isLight ? '0,47,167' : '245,166,35'} isLight={isLight} />

          <div className="w-full max-w-2xl">
            <div
              className="rounded-[6px] px-6 sm:px-8 py-6 text-center relative overflow-hidden"
              style={{
                border: `1.5px solid ${T.text}`,
                background: isLight
                  ? 'linear-gradient(135deg, #ffffff 0%, #F4F2EC 100%)'
                  : 'linear-gradient(135deg, #111116 0%, #0a0a0b 100%)',
                boxShadow: isLight
                  ? `0 10px 30px rgba(0,47,167,0.18), inset 0 0 0 1px rgba(0,47,167,0.08)`
                  : `0 10px 30px rgba(245,166,35,0.12), inset 0 0 30px rgba(0,0,0,0.4)`,
              }}
            >
              <div className="font-mono uppercase mb-2" style={{ fontSize: 13, letterSpacing: '0.22em', color: T.text, fontWeight: 700 }}>
                Shared trust infrastructure
              </div>
              <div className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: '0.15em', color: T.textMuted, lineHeight: 2, fontWeight: 600 }}>
                Authentication · compliance · escrow · provenance · custody · release conditions
              </div>
              <div className="font-mono mt-2" style={{ fontSize: 9, letterSpacing: '0.12em', color: T.accent, fontWeight: 600 }}>
                The moat: trust, workflow ownership, transaction data
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company cards */}
      <section className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="font-mono uppercase mb-1.5" style={{ fontSize: 8, letterSpacing: '0.3em', color: T.textFaint }}>
          The Companies
        </div>
        <div className="flex items-center gap-4 mb-8">
          <span className="font-mono uppercase" style={{ fontSize: 11, letterSpacing: '0.2em', color: T.purple }}>
            Trust, money, custody, succession, transfer
          </span>
          <div className="h-px flex-1" style={{ background: `linear-gradient(to right, ${T.border}, transparent)` }} />
        </div>

        <div className="space-y-4">
          <CompanyCard
            num="01 · Art Market"
            name="TrustFrame"
            accent={T.purple}
            badge="Trust as a Service"
            summary="Art-world trust, payment protection, and dispute-safe transaction handling delivered as a managed service. Protects artists, collectors, galleries, advisors, and institutions by handling payment protection, transaction records, title clarity, consignment safeguards, release conditions, and dispute-safe documentation."
            italic="TrustFrame is not simply escrow software. It is trust as a service for the art world."
            links={[
              { label: 'trustframe.art', href: 'https://www.trustframe.art' },
              { label: 'trustframe.company', href: 'https://trustframe.company' },
            ]}
            outcome="Protected art transactions"
            users="Artists, collectors, galleries, advisors, estates, institutions, art lawyers"
            moat="Trust, workflow ownership, legal/transaction expertise, transaction data"
          />
          <CompanyCard
            num="02 · Storefront"
            name="Monyx.cafe"
            accent="#a78bfa"
            badge="The Front Door"
            summary="The public-facing storefront for the TrustFrame family. Where users discover, enter, and interact with the trust economy: art, custody, money movement, succession, wealth transfer, and protected transactions. Simple, accessible, human, and brand-led."
            links={[{ label: 'monyx.cafe', href: 'https://monyx.cafe' }]}
            outcome="A clear, elegant way to access protected financial and transaction services"
            users="The storefront"
          />
          <CompanyCard
            num="03 · Financial Layer"
            name="Monyx.money"
            accent="#34d399"
            badge="Wallet → Bank"
            summary="The financial wallet layer of the TrustFrame ecosystem. Begins as a wallet for protected transactions and evolves into a banking layer for creators, collectors, families, estates, founders, and high-trust communities. The long-term ambition: a trusted financial operating layer for people who need custody, transfer, payment protection, and multi-party transaction confidence."
            links={[{ label: 'monyx.money', href: 'https://monyx.money' }]}
            outcome="Money held, moved, protected, and released with trust"
            users="The wallet, payment, custody, and future banking layer"
          />
          <CompanyCard
            num="04 · Wealth Transfer"
            name="Custos"
            accent="#22d3ee"
            badge="Wealth Transfer Management"
            summary="Wealth transfer management for families, estates, and trusted assets. Built for the movement of value between generations, families, beneficiaries, advisors, and custodians. Helps families and advisors organise assets, intentions, documents, beneficiaries, responsibilities, and transfer events into a single trusted framework."
            italic="Custos is not just estate planning software. It is wealth transfer management delivered as a guided service."
            links={[{ label: 'custos.rsvp', href: 'https://custos.rsvp' }]}
            outcome="Clarity, continuity, and control over wealth transfer"
            users="Families, founders, collectors, trustees, private client lawyers, wealth advisors, family offices"
            moat="Trust, family workflows, custody logic, sensitive asset data"
          />
          <CompanyCard
            num="05 · Succession · succession.global"
            name="Custodian"
            accent="#f472b6"
            badge="Not a listing. A handover."
            summary="A confidentiality-first marketplace that matches retiring owners with vetted successors — and runs every deal to completion. Built for owners of valuable businesses, workshops, practices, properties, or specialist operations who need the right person, family, buyer, operator, or successor to continue what they started."
            italic="The market does not need another business-for-sale website. It needs a trusted transition layer where legacy, confidentiality, fit, financing, diligence, and completion are managed properly."
            links={[{ label: 'custodian.network', href: 'https://custodian.network' }]}
            outcome="Successful succession and business handover"
            users="Retiring owners, family businesses, operators, successors, investors, family offices"
            moat="Confidential deal flow, vetted successor network, handover workflows, completion management"
          />
        </div>

        <div className="text-center mt-16 pt-10" style={{ borderTop: `1px solid ${T.border}` }}>
          <div className="font-mono uppercase mb-3" style={{ fontSize: 8, letterSpacing: '0.3em', color: T.textFaint }}>
            The TrustFrame Mission
          </div>
          <p className="font-mono max-w-2xl mx-auto leading-[1.9]" style={{ fontSize: 13, letterSpacing: '0.08em', color: T.text }}>
            Trust, money, custody, succession, and transfer infrastructure<br />
            <span style={{ color: T.textMuted }}>for the modern wealth economy.</span>
          </p>
        </div>
      </section>

      {/* ── The three products de-risk each other ─────────────── */}
      <section className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="font-mono uppercase mb-1.5" style={{ fontSize: 8, letterSpacing: '0.3em', color: T.textFaint }}>
          The Three Products
        </div>
        <div className="flex items-center gap-4 mb-6">
          <h2
            className="font-machina font-bold uppercase"
            style={{ fontSize: 'clamp(20px, 3vw, 28px)', letterSpacing: '0.1em', color: T.text }}
          >
            They de-risk each other
          </h2>
          <div className="h-px flex-1" style={{ background: `linear-gradient(to right, ${T.border}, transparent)` }} />
        </div>
        <p className="font-sans leading-[1.9] max-w-3xl mb-8" style={{ fontSize: 13, color: T.textMuted }}>
          A work enrolled in <span style={{ color: T.green }}>Studio</span> is provenanced. That same record makes it{' '}
          <span style={{ color: T.blue }}>insurable</span> and makes it good{' '}
          <span style={{ color: T.accent }}>collateral</span>. The data that prices the insurance also prices the loan — so each product strengthens the others' underwriting.{' '}
          <em className="not-italic" style={{ color: T.text }}>That is a moat, not a bundle.</em>
        </p>

        {/* Loop diagram */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 md:gap-4 items-stretch mb-6">
          {[
            { name: 'Studio enrols', desc: 'A verified, provenanced record is created once.', color: T.green, Icon: Images },
            { name: 'TrusTEE lends', desc: 'Recovery data proves what works are really worth.', color: T.accent, Icon: Diamond },
            { name: 'Schedule insures', desc: 'An insured, documented work is lower-risk collateral.', color: T.blue, Icon: Certificate },
          ].map((p, i, arr) => (
            <React.Fragment key={p.name}>
              <div
                className="rounded-[6px] p-5 sm:p-6 relative overflow-hidden"
                style={{ background: T.surface, border: `1px solid ${T.border}` }}
              >
                <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: p.color, opacity: 0.6 }} />
                <p.Icon size={22} weight="duotone" style={{ color: p.color }} />
                <h3 className="font-machina font-bold uppercase mt-3 mb-2" style={{ fontSize: 14, letterSpacing: '0.1em', color: p.color }}>
                  {p.name}
                </h3>
                <p className="font-sans leading-[1.6]" style={{ fontSize: 12, color: T.textMuted }}>
                  {p.desc}
                </p>
              </div>
              {i < arr.length - 1 && (
                <div className="hidden md:flex items-center justify-center" aria-hidden>
                  <ArrowRight size={20} weight="bold" style={{ color: T.accent }} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* The loop callout */}
        <div
          className="rounded-[6px] p-5 flex items-start gap-4"
          style={{ background: T.bgInk, border: `1px solid ${T.border}` }}
        >
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
            style={{ background: `${T.green}1f`, border: `1px solid ${T.green}66` }}
          >
            <Check size={16} weight="bold" style={{ color: T.green }} />
          </div>
          <p className="font-sans leading-[1.7] pt-1" style={{ fontSize: 13, color: T.text }}>
            <span style={{ color: T.accent }}>The loop:</span> same data underwrites cover and loan-to-value. The register's integrity is the whole advantage — and it widens every time a product runs on it.
          </p>
        </div>
      </section>

      {/* ── Studio leads. TrusTEE next. Schedule third. ────────── */}
      <section className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="font-mono uppercase mb-1.5" style={{ fontSize: 8, letterSpacing: '0.3em', color: T.accent }}>
          Sequencing
        </div>
        <div className="flex items-center gap-4 mb-8">
          <h2
            className="font-machina font-bold uppercase"
            style={{ fontSize: 'clamp(20px, 3vw, 28px)', letterSpacing: '0.08em', color: T.text }}
          >
            Studio leads. TrusTEE next. Schedule third.
          </h2>
          <div className="h-px flex-1" style={{ background: `linear-gradient(to right, ${T.border}, transparent)` }} />
        </div>

        <div className="space-y-3">
          {[
            {
              n: '01',
              name: 'TrustFrame Studio',
              tagColor: T.green,
              tag: 'NOW — UNREGULATED',
              Icon: Images,
              body: 'The enrolment engine. No regulatory gate. Feeds both regulated products. Resource this first and hardest.',
            },
            {
              n: '02',
              name: 'TrusTEE',
              tagColor: T.accent,
              tag: 'NEXT — FAST CASH, PROTOTYPE READY',
              Icon: Diamond,
              body: 'Pawn earns from day one. iOS prototype already built. Generates the recovery data that proves the pool — but is capital-hungry.',
            },
            {
              n: '03',
              name: 'TrustFrame Schedule',
              tagColor: T.blue,
              tag: 'THIRD — DEEPEST MOAT',
              Icon: Certificate,
              body: 'Recurring premium and the strongest data advantage. Lighter to launch, slower to pay. Walks in pre-validated by Studio + TrusTEE.',
            },
          ].map((row) => (
            <div
              key={row.n}
              className="rounded-[6px] grid grid-cols-[auto_auto_1fr] sm:grid-cols-[auto_auto_minmax(220px,2fr)_3fr] gap-4 sm:gap-6 items-center p-5 sm:p-6"
              style={{ background: T.surface, border: `1px solid ${T.border}` }}
            >
              <div
                className="font-machina font-bold opacity-30 select-none"
                style={{ fontSize: 'clamp(28px,4vw,40px)', color: T.text, letterSpacing: '0.02em' }}
              >
                {row.n}
              </div>
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{ background: `${row.tagColor}1a`, border: `1px solid ${row.tagColor}55` }}
              >
                <row.Icon size={18} weight="duotone" style={{ color: row.tagColor }} />
              </div>
              <div>
                <h3 className="font-machina font-bold uppercase mb-1.5" style={{ fontSize: 15, letterSpacing: '0.1em', color: T.text }}>
                  {row.name}
                </h3>
                <span
                  className="inline-block font-mono uppercase"
                  style={{
                    fontSize: 8,
                    letterSpacing: '0.18em',
                    color: row.tagColor,
                    border: `1px solid ${row.tagColor}55`,
                    background: `${row.tagColor}0d`,
                    padding: '3px 8px',
                  }}
                >
                  {row.tag}
                </span>
              </div>
              <p className="font-sans leading-[1.7] col-span-3 sm:col-span-1" style={{ fontSize: 12.5, color: T.textMuted }}>
                {row.body}
              </p>
            </div>
          ))}
        </div>

        {/* Three product cards (Studio / TrusTEE / Schedule) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mt-8">
          {[
            { name: 'TrustFrame Studio', label: 'ARTIST CATALOGUE', color: T.green, Icon: Images, body: 'Enrolment, provenance & certification surface.' },
            { name: 'TrusTEE', label: 'LUXURY PAWN', color: T.accent, Icon: Diamond, body: 'Asset-backed lending against verified works.' },
            { name: 'TrustFrame Schedule', label: 'ART INSURANCE', color: T.blue, Icon: Certificate, body: 'Cover activated from the scheduled register.' },
          ].map((p) => (
            <div
              key={p.name}
              className="rounded-[6px] p-6 flex flex-col items-start gap-4 transition-transform duration-300 hover:-translate-y-0.5"
              style={{ background: T.surface, border: `1px solid ${T.border}` }}
            >
              <div className="flex items-center gap-3 self-stretch">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: `${p.color}14`, border: `1px solid ${p.color}40` }}
                >
                  <p.Icon size={20} weight="duotone" style={{ color: p.color }} />
                </div>
                <div>
                  <h3 className="font-machina font-bold uppercase" style={{ fontSize: 14, letterSpacing: '0.1em', color: T.text }}>
                    {p.name}
                  </h3>
                  <p className="font-mono uppercase mt-1" style={{ fontSize: 8, letterSpacing: '0.22em', color: p.color }}>
                    {p.label}
                  </p>
                </div>
              </div>
              <div className="h-px w-12" style={{ background: p.color, opacity: 0.5 }} />
              <p className="font-sans leading-[1.7]" style={{ fontSize: 12.5, color: T.textMuted }}>
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </section>
    </PrivateLayout>
  );
}
